import React, {useEffect, useState} from 'react';
import b_ from 'b_';
import {genVideoLink} from '@yandex-int/pythia-libs/lib/common-components';
import {useForm, useFormContext} from 'react-hook-form';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';
import {Upload} from 'tus-js-client';
import {Progress} from '@yandex-lego/components/Progress/desktop';
import useMount from 'react-use/lib/useMount';
import usePrevious from 'react-use/lib/usePrevious';

import I18N from 'assets/i18n/brandlift-project';

import {CreativeCreateData} from 'client/creatives/types';
import {CreativeInfo} from 'client/creatives/utils/parse';
import {BordomatikBox, CreativeBox, Survey, SurveySlug, UserInfo} from 'client/common/types';

import Block from 'client/common/components/block';
import VideoUpload from 'client/common/components/video-upload';
import ClientSelect from 'client/common/components/client-select';
import IconButton from 'client/common/components/icon-button';
import {checkPayed} from 'client/common/utils/payment';
import {getVideoInfo, parseVideoLink, VideoErrorReason} from 'client/common/utils/video';
import cfg from 'client/common/utils/client-config';
import {Spin, Textinput} from 'client/common/components/lego';
import {usePaymentStatus} from 'client/common/hooks/payment';
import {useCheckForShortId, useCheckVHVideoState} from 'client/common/hooks/resources';

import {setImmut} from './utils';
import StateBadge from './state-badge';
import {CreativeFormValues, UploadState} from './types';

import VideoErrorIcon from './icons/video-error.svg';

const i18n = I18N.creatives;
const b = b_.with('creative-settings-form');

const resourceOrigin =
    {
        production: 'https://frontend.vh.yandex.ru'
    }[process.env.NODE_ENV!] || 'https://vh.test.yandex.ru';

function getErrorMessageByReason(reason: VideoErrorReason) {
    switch (reason) {
        case 'DURATION_TOO_LONG':
            return i18n.creativeLoader.error.DURATION_TOO_LONG.replace(
                '%s',
                String(cfg.creative.minDuration)
            ).replace('%s', String(cfg.creative.maxDuration));
        case 'SIZE_TOO_BIG':
            return i18n.creativeLoader.error.SIZE_TOO_BIG.replace(
                '%s',
                String(cfg.creative.maxSizeMb)
            );
        default:
            return i18n.creativeLoader.error[reason];
    }
}

interface UseFormProps {
    box: CreativeBox | BordomatikBox;
    updateSurveyBoxContext: (box: unknown) => Promise<void>;
}

// eslint-disable-next-line max-statements
function useFormProps(props: UseFormProps) {
    const {box, updateSurveyBoxContext} = props;
    const [error, setError] = useState('');
    const [uploadState, setUploadState] = useState(
        box?.context?.creative?.id ? UploadState.done : UploadState.ready
    );
    const [upload, setUpload] = useState<Upload>();

    // eslint-disable-next-line @typescript-eslint/unbound-method
    const {setValue, clearErrors} = useFormContext<CreativeFormValues>();

    const handleDurationUpdated = async (_: string, timeout: number) => {
        await updateSurveyBoxContext(setImmut(box, 'context.creative.duration', timeout));
    };

    const onError = (msg: string) => {
        setError(msg);
        setUpload(undefined);

        if (msg) {
            setUploadState(UploadState.error);
        } else {
            setUploadState(box?.context?.creative?.id ? UploadState.done : UploadState.ready);
        }
    };

    const handleUploadStart = (_: string, __: File['name']) => {
        setUploadState(UploadState.progress);
    };

    const handleSaveUploadInstance = (_: string, uploadInstance?: Upload) => {
        setUpload(uploadInstance);
    };

    const handleUploadSuccess = async (
        title: string,
        duration: number,
        data: {resourceId: string}
    ) => {
        const creative = cloneDeep(box?.context?.creative);

        await updateSurveyBoxContext(
            setImmut(box, 'context.creative', {
                ...creative,
                title,
                service: 'resource',
                duration,
                id: data.resourceId
            })
        );
        setUploadState(UploadState.done);
        setUpload(undefined);
    };

    // eslint-disable-next-line max-statements
    const handleAdd = async (url: string) => {
        if (!url) {
            setUploadState(UploadState.ready);

            return;
        }

        setUploadState(UploadState.progress);
        clearErrors('videoLink');

        const video = parseVideoLink(url);
        const creative = cloneDeep(box?.context?.creative);

        const id = video?.id;
        const service = video?.service;

        if (!service || !id) {
            return onError(i18n.creativeLoader.invalidUrl);
        }

        const info = await getVideoInfo(service, id);

        if ('error' in info) {
            const reason = info.error?.reason ?? 'UPLOAD_ERROR';

            return onError(getErrorMessageByReason(reason));
        }

        await updateSurveyBoxContext(
            setImmut(box, 'context.creative', {
                ...creative,
                ...(pick(info, ['service', 'id', 'title', 'duration']) as CreativeCreateData)
            })
        );

        setUploadState(UploadState.done);
    };

    const handleRemove = async () => {
        await upload?.abort();
        await updateSurveyBoxContext(setImmut(box, 'context.creative', undefined));
        setUploadState(UploadState.ready);
        setValue('videoLink', '');
    };

    const handleError = (code: string) => {
        onError('');

        switch (code) {
            case 'file-too-large':
                onError(getErrorMessageByReason('SIZE_TOO_BIG'));
                break;
            case 'duration-too-long':
                onError(getErrorMessageByReason('DURATION_TOO_LONG'));
                break;
            case 'upload-error':
                onError(getErrorMessageByReason('UPLOAD_ERROR'));
                break;
            case 'too-many-files':
                onError(i18n.creativeLoader.error.TOO_MANY_FILES);
                break;
            default:
                console.error(code);
                break;
        }
    };

    return {
        uploadState,
        error,
        handleDurationUpdated,
        handleUploadStart,
        handleUploadSuccess,
        handleSaveUploadInstance,
        handleAdd,
        handleRemove,
        handleError
    };
}

const videoErrors: Record<string, string> = {
    [UploadState.progress]: i18n.creativeSettings.videoErrors.isBeingUploaded,
    [UploadState.done]: i18n.creativeSettings.videoErrors.isBeingProcessed
};

type VideoBlockProps = UseFormProps & {
    slug: SurveySlug;
    label: string;
    description: string;
    disabled: boolean;
    creative: CreativeInfo;
    state: Survey['state'];
    userInfo?: UserInfo;
    register: ReturnType<typeof useForm>['register'];
    errors: ReturnType<typeof useForm>['errors'];
    setValue?: (name: string, value: unknown) => void;
    clearErrors?: (name: string) => void;
};

// eslint-disable-next-line complexity, max-statements
export default function VideoBlock(props: VideoBlockProps) {
    const {
        box,
        label,
        description,
        disabled,
        creative,
        state,
        slug,
        register,
        errors,
        updateSurveyBoxContext,
        userInfo,
        setValue,
        clearErrors
    } = props;

    const {data: paymentStatus} = usePaymentStatus(slug);
    const [clientId, setClientId] = useState<string>();
    const [[current, total], setUploadProgress] = useState<[number, number]>([0, 0]);
    const [link, setLink] = useState<string | undefined>('');
    const [isSpinnerEnabled, setIsSpinnerEnabled] = useState(false);
    const [displayVideoError, setDisplayVideoError] = useState(false);
    const isAgency = userInfo?.authInfo?.isAgency;
    const canUpload = !isAgency || Boolean(clientId);

    const resourceId =
        box.context?.creative?.service === 'resource' ? box.context?.creative?.id : undefined;

    const creativeState = useCheckVHVideoState(resourceId, creative.state);

    const {
        uploadState,
        error,
        handleDurationUpdated,
        handleUploadStart,
        handleUploadSuccess,
        handleSaveUploadInstance,
        handleAdd,
        handleRemove,
        handleError
    } = useFormProps({
        box,
        updateSurveyBoxContext
    });

    const handleUploadProgress = (bytesUploaded: number, bytesTotal: number) => {
        setUploadProgress([bytesUploaded, bytesTotal]);
    };

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        void handleAdd(e.target.value);
        setLink(e.target.value);
    };

    const handleCancel = () => {
        setLink('');
        void handleRemove();
    };
    const isVideoReadyForPublish = () => {
        if (!box?.context?.creative?.id || error) {
            return false;
        }

        return displayVideoError;
    };

    const shortId = useCheckForShortId(resourceId);
    const prevShortId = usePrevious(shortId);
    const prevCreativeState = usePrevious(creativeState);

    useMount(() => {
        if (creative.id && creative.state === 'ready') {
            const initlink = genVideoLink({
                service: creative.service,
                id: creative.id,
                resourceOrigin
            });

            setLink(initlink);
        }
    });

    useEffect(() => {
        if (shortId && !link && shortId !== prevShortId) {
            const link1 = genVideoLink({
                service: creative.service,
                id: shortId,
                resourceOrigin
            });

            setLink(link1);
        }
    }, [shortId, creative.service, link, prevShortId]);

    useEffect(() => {
        if (setValue) setValue('videoLink', link);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link]);

    useEffect(() => {
        if (uploadState === UploadState.done && creative.service === 'youtube') {
            if (clearErrors) clearErrors('videoLink');
            setLink(
                genVideoLink({
                    service: creative.service,
                    id: creative.id,
                    resourceOrigin
                })
            );
        }
        if (
            (creative.state === 'ready' && uploadState === UploadState.done) ||
            (prevCreativeState === 'ready' &&
                creative.state === 'new' &&
                uploadState === UploadState.done)
        ) {
            setDisplayVideoError(true);
        } else {
            setDisplayVideoError(false);
        }
    }, [
        uploadState,
        creative.service,
        creative.id,
        clearErrors,
        creative.state,
        prevCreativeState
    ]);

    useEffect(() => {
        if (['new', 'transcoding'].includes(creativeState)) {
            setIsSpinnerEnabled(true);
        } else {
            setIsSpinnerEnabled(false);
        }

        if (prevCreativeState === 'transcoding' && creativeState === 'ready') {
            if (clearErrors) clearErrors('videoLink');
        }
    }, [creativeState, prevCreativeState, clearErrors]);

    const isVideoInputDisabled =
        disabled || uploadState === UploadState.done || uploadState === UploadState.progress;
    const badgeProps = {surveyState: state, creativeState, isPayed: checkPayed(paymentStatus)};

    return (
        <Block header={label} description={description}>
            {userInfo?.authInfo?.isAgency && (
                <ClientSelect
                    className={b('agency-client-select')}
                    selectedId={clientId}
                    onChange={id => setClientId(id)}
                    showRemainPackages
                />
            )}
            <Textinput
                className={b('video-link-field')}
                size='m'
                placeholder={i18n.creativeSettings.new.video.link}
                defaultValue={(link as string) || ''}
                name='videoLink'
                disabled={isVideoInputDisabled}
                onBlur={e => handleOnBlur(e)}
                view='dl'
                controlRef={register({
                    validate: {
                        videoReadyForPublish: isVideoReadyForPublish
                    }
                })}
            />
            {uploadState === UploadState.done && (
                <div className={b('video-upload-progress')}>
                    <div className={b('preview')}>
                        {creative.previewUrl ? (
                            <img src={creative.previewUrl} />
                        ) : (
                            <>
                                {isSpinnerEnabled ? (
                                    <Spin progress position='center' view='default' size='m' />
                                ) : (
                                    <iframe
                                        src={link}
                                        frameBorder='0'
                                        scrolling='no'
                                        allow='autoplay; fullscreen; encrypted-media'
                                        allowFullScreen
                                    />
                                )}
                            </>
                        )}
                        <IconButton
                            className={b('video-upload-icon', {type: 'cross'})}
                            onClick={handleCancel}
                            type='cross-light'
                        />
                    </div>
                    <StateBadge {...badgeProps} />
                </div>
            )}
            {uploadState === UploadState.ready && (
                <VideoUpload
                    className={b('video-upload')}
                    clientId={clientId}
                    disabled={!canUpload}
                    minDurationSec={cfg.creative.minDuration}
                    maxDurationSec={cfg.creative.maxDuration}
                    maxSizeMb={cfg.creative.maxSizeMb}
                    maxFiles={1}
                    onUploadInstanceReady={handleSaveUploadInstance}
                    onDurationUpdated={handleDurationUpdated}
                    onStart={handleUploadStart}
                    onProgress={handleUploadProgress}
                    onSuccess={handleUploadSuccess}
                    onError={e => handleError(e.code)}
                >
                    <div className={b('video-upload-text')}>
                        {i18n.creativeSettings.new.video.uploadDropareaText}
                    </div>
                    <div className={b('video-upload-btn')}>
                        {i18n.creativeSettings.new.video.uploadBtn}
                    </div>
                </VideoUpload>
            )}
            {uploadState === UploadState.progress && (
                <div className={b('video-upload-progress')}>
                    <div className={b('video-upload-progress-preview')}>
                        <Progress
                            className={b('video-upload-progress-bar')}
                            value={current}
                            maxValue={total}
                        />
                        <IconButton
                            className={b('video-upload-icon', {type: 'cross'})}
                            onClick={handleCancel}
                            type='cross-light'
                        />
                    </div>
                    <StateBadge
                        title={i18n.creativeSettings.new.video.progress.label}
                        label={i18n.creativeSettings.new.video.progress.desc}
                        {...badgeProps}
                    />
                </div>
            )}
            {uploadState === UploadState.error && (
                <div className={b('video-upload-progress')}>
                    <div className={b('video-upload-progress-preview')}>
                        <img src={VideoErrorIcon} className={b('video-error-logo')} />
                        <IconButton
                            className={b('video-upload-icon', {type: 'cross'})}
                            onClick={handleCancel}
                            type='cross-light'
                        />
                    </div>
                    <StateBadge
                        title={i18n.creativeSettings.new.video.error.label}
                        label={error}
                        type='error'
                        {...badgeProps}
                    />
                </div>
            )}
            {errors.videoLink?.type === 'videoReadyForPublish' && (
                <span className={b('errors')}>
                    {videoErrors[uploadState] || i18n.creativeSettings.videoErrors.isRequired}
                </span>
            )}
        </Block>
    );
}
